import { useMemo, useCallback } from 'react';
import { isValid, format } from 'date-fns';
import { Checkbox, Text, Status } from '@/components/AutoCompleteGenericElements';
import useCandidateStatuses from '@/hooks/useCandidateStatuses';
import { fieldsAndFriendlyLabelsForFilters } from '@/pages/CandidateInfo/candidateInfoFields';
import { TechStackType, AvailableRoles } from '@/types';
import { User } from '@/types/api';
import CandidateFilterComponent, { CandidateFilterProps } from './CandidateFilter.component';
import { candidateFilters, customRangeOptions } from './CandidateFilter.utils';

type CandidateFilterType = Pick<
  CandidateFilterProps,
  | 'filter'
  | 'onSelectFilterOption'
  | 'onRemoveFilterOption'
  | 'onRemoveFilterHandler'
  | 'onClickCondition'
  | 'onClickValue'
> & {
  showButtonToDelete: () => void;
  id?: string;
  technologiesOptions: TechStackType[];
  allUsers: User[];
};

const CandidateFilter = ({
  filter,
  onSelectFilterOption,
  showButtonToDelete,
  onRemoveFilterHandler,
  technologiesOptions,
  allUsers,
  ...props
}: CandidateFilterType): JSX.Element => {
  const candidateStatuses = useCandidateStatuses();

  const candidateOnwers = useMemo(() => {
    return allUsers
      .filter((user) => {
        return user.roles?.some((role) => role.value === AvailableRoles.HIRING_ADMIN);
      })
      .map((user) => ({
        value: user?.id as string,
        node: (elementProps) => <Text {...elementProps}>{user?.name}</Text>,
      }));
  }, [allUsers]);

  const techStacksOptions = useMemo(() => {
    return technologiesOptions.map((tech) => ({
      value: tech?.name,
      node: (elementProps) => (
        <Checkbox
          onClick={(event) => event.stopPropagation()}
          label={tech?.name}
          {...elementProps}
        />
      ),
    }));
  }, [technologiesOptions]);

  const statusOptions = useMemo(() => {
    return candidateStatuses.map((groupedStatuses) => ({
      label: groupedStatuses.label,
      options: groupedStatuses.options.map((status) => ({
        value: status,
        node: (elementProps) => <Status {...elementProps} status={status} />,
      })),
    }));
  }, [candidateStatuses]);

  const searchForCandidates = useMemo(() => {
    return filter.field === fieldsAndFriendlyLabelsForFilters.firstName && !!filter.condition;
  }, [filter.condition, filter.field]);

  const isDateRange = useMemo(() => {
    return filter.value === 'custom-range';
  }, [filter.value]);

  const isDatePicker = useMemo(() => {
    return filter.value === 'specific-day';
  }, [filter.value]);

  const withSearch = useMemo(() => {
    const fieldsWithInputSearch = [
      fieldsAndFriendlyLabelsForFilters.skills,
      fieldsAndFriendlyLabelsForFilters.country,
    ];
    const hasCondition = filter.condition;
    return fieldsWithInputSearch.includes(filter.field) && hasCondition;
  }, [filter.field, filter.condition]);

  const getPropertyFields = useMemo(() => {
    return candidateFilters.map(({ property }) => ({
      value: property,
      node: (elementProps) => <p {...elementProps}>{property}</p>,
    }));
  }, []);

  const doesPropertyMatchWithField = useCallback(
    ({ property }): boolean => property === filter.field,
    [filter.field]
  );

  const getConditionFields = useMemo(() => {
    const pickedOptionByField = candidateFilters.find(doesPropertyMatchWithField);

    return pickedOptionByField?.conditions ?? [];
  }, [doesPropertyMatchWithField]);

  const getValuesFields = useMemo(() => {
    const isValueCustomRange = (value): boolean => value.value === 'custom-range';
    const isValueNotCustomRange = (value): boolean => value.value !== 'custom-range';

    const pickedOptionByField = candidateFilters.find(doesPropertyMatchWithField);
    const pickedOptions = pickedOptionByField?.values ?? [];

    if (pickedOptionByField?.property === fieldsAndFriendlyLabelsForFilters.skills) {
      return techStacksOptions;
    }

    if (pickedOptionByField?.property === fieldsAndFriendlyLabelsForFilters.ownerId) {
      return candidateOnwers;
    }

    if (filter.condition === 'isWithin') {
      return [...customRangeOptions, ...pickedOptions.filter(isValueCustomRange)];
    }

    return pickedOptions.filter(isValueNotCustomRange);
  }, [candidateOnwers, doesPropertyMatchWithField, filter.condition, techStacksOptions]);

  const getGroupedValuesFields = useMemo(() => {
    return statusOptions ?? [];
  }, [statusOptions]);

  const getAutocompleteOptions = useMemo(() => {
    const { field, condition } = filter;

    if (!field) {
      return getPropertyFields;
    }

    if (field && !condition) {
      return getConditionFields;
    }

    if (field && condition) {
      return getValuesFields;
    }

    return [];
  }, [filter, getValuesFields, getConditionFields, getPropertyFields]);

  const valueElement = useMemo(() => {
    const doesValueMatch = (option): boolean => option.value === filter.value;

    return getValuesFields.find(doesValueMatch)?.node;
  }, [filter.value, getValuesFields]);

  const conditionElement = useMemo(() => {
    const doesConditionMatch = (option): boolean => option.value === filter.condition;

    return getConditionFields.find(doesConditionMatch)?.node;
  }, [filter.condition, getConditionFields]);

  const onOptionsClick = useCallback(
    (event): void => {
      event.stopPropagation();
      showButtonToDelete();
    },
    [showButtonToDelete]
  );

  const onSelectDate = (startDate: Date, endDate?: null | Date): void => {
    const isStartDateValid = isValid(startDate);
    const isEndDateValid = isValid(endDate);
    const startDateFormatted = isStartDateValid && format(new Date(startDate), 'yyyy-MM-dd');
    const endDateFormatted = isEndDateValid && endDate && format(new Date(endDate), 'yyyy-MM-dd');

    if (isEndDateValid) {
      onSelectFilterOption([startDateFormatted, endDateFormatted]);
    }

    if (startDateFormatted) {
      onSelectFilterOption(startDateFormatted);
    }
  };

  return (
    <CandidateFilterComponent
      searchForCandidates={searchForCandidates}
      isDateRange={isDateRange}
      isDatePicker={isDatePicker}
      withSearch={!!withSearch}
      onOptionsClick={onOptionsClick}
      onSelectDate={onSelectDate}
      onSelectFilterOption={onSelectFilterOption}
      filter={filter}
      onRemoveFilterHandler={onRemoveFilterHandler}
      selectedOptions={Array.isArray(filter.value) ? filter.value : []}
      options={getAutocompleteOptions}
      groupedOptions={getGroupedValuesFields}
      isDateType={isDateRange || isDatePicker}
      valueElement={valueElement}
      conditionElement={conditionElement}
      {...props}
    />
  );
};

export default CandidateFilter;
